import FingerprintJS from "@fingerprintjs/fingerprintjs"
import {load as loadBotd} from "@fingerprintjs/botd"
import {UAParser} from "ua-parser-js"
import {getAiAgentSignals} from "./behavioralSignals"

const fpPromise = FingerprintJS.load()
const botdPromise = loadBotd()

const EMULATOR_RENDERER_PATTERN = /goldfish|ranchu|android emulator|bluestacks|genymotion/i
const VM_RENDERER_PATTERN = /vmware|virtualbox|parallels|llvmpipe|swiftshader/i

// Below this, treat navigator.storage.estimate()'s quota as an incognito/private-mode signal.
// Not reliable on all browsers (some have patched the quota tell since ~2021) - best-effort only.
const INCOGNITO_QUOTA_THRESHOLD_BYTES = 120 * 1024 * 1024

function componentValue(component) {
    return component && "value" in component ? component.value : undefined
}

// ANGLE is Chrome's cross-platform GPU abstraction - it shows up on Windows, macOS, Android
// and Linux alike (e.g. "ANGLE (Apple, ANGLE Metal Renderer: Apple M1, ...)"), so a bare "angle"
// match is not itself platform-specific. Check vendor/backend tokens first and only treat
// Direct3D (Windows-only) as a Windows signal.
function inferPlatformFromRenderer(renderer) {
    if (/metal/i.test(renderer)) return "macOS"
    if (/adreno|mali-/i.test(renderer)) return "Android"
    if (/mesa|llvmpipe/i.test(renderer)) return "Linux"
    if (/direct3d/i.test(renderer)) return "Windows"
    return null
}

// Checks android/iOS before mac/linux since substring checks would otherwise misfire (see below),
// and is reused against navigator.platform, navigator.userAgent, and userAgentData.platform alike.
function normalizePlatform(platform, maxTouchPoints = 0) {
    const p = platform.toLowerCase()
    if (p.includes("android")) return "Android"
    if (p.includes("iphone") || p.includes("ipod")) return "iOS"
    if (p.includes("cros")) return "ChromeOS"
    if (p.includes("win")) return "Windows"
    if (p.includes("mac") || p.includes("ipad")) {
        // iPadOS 13+ requests the desktop Safari UA/platform ("Macintosh"/"MacIntel"), making an
        // iPad indistinguishable from a real Mac by string alone. Real Macs report
        // maxTouchPoints <= 1 (0, or 1 for some trackpads); a multi-touch "Mac" is an iPad.
        return maxTouchPoints > 1 ? "iOS" : "macOS"
    }
    if (p.includes("linux") || p.includes("x11")) return "Linux"
    return null
}

// UA-CH's `model` hint is Android-only by spec - Chromium reports "" everywhere else, and
// Firefox/Safari don't implement userAgentData at all. Fall back to the GPU renderer string as
// a hardware proxy (covers desktop, and Android when UA-CH itself is unavailable).
function getRendererDeviceModelFallback(renderer) {
    return renderer || null
}

// Safari never exposes UA-CH, so on iOS we can't get a real model - fall back to the coarse
// device family instead of a raw GPU renderer string (which is meaningless as a "model" there).
function getIosDeviceFamily(userAgent, maxTouchPoints) {
    if (/ipad/i.test(userAgent)) return "iPad"
    if (/iphone/i.test(userAgent)) return "iPhone"
    if (/ipod/i.test(userAgent)) return "iPod"
    // iPadOS 13+ requests the desktop UA, so a touch-capable "Macintosh" UA is an iPad in
    // disguise rather than a real Mac.
    if (maxTouchPoints > 1) return "iPad"
    return "iOS Device"
}

// Chrome freezes the Windows UA string at "10.0" and instead signals Windows 11 through
// platformVersion's major component (>=13). See:
// https://learn.microsoft.com/en-us/microsoft-edge/web-platform/how-to-detect-win11
function mapWindowsPlatformVersion(platformVersion) {
    const major = parseInt(platformVersion.split(".")[0], 10)
    if (isNaN(major)) return platformVersion
    if (major >= 13) return "11"
    if (major >= 3) return "10"
    if (major === 2) return "8.1"
    if (major === 1) return "8"
    return "7"
}

function getOsVersion(platform, userAgentData, uaParserOsVersion) {
    if (userAgentData?.platformVersion) {
        return platform === "Windows"
            ? mapWindowsPlatformVersion(userAgentData.platformVersion)
            : userAgentData.platformVersion
    }
    return uaParserOsVersion || null
}

function getCpuArchitecture(userAgentData) {
    if (!userAgentData?.architecture) return null
    return userAgentData.bitness ? `${userAgentData.architecture}${userAgentData.bitness}` : userAgentData.architecture
}

function getIsMetadataMismatch(platformSignals) {
    const platforms = platformSignals.filter((p) => Boolean(p))
    return new Set(platforms).size > 1
}

// userAgentData.mobile is explicit but Chromium-only. Where we already have a normalized
// platform (from UA-CH, the full UA string, or navigator.platform), Android/iOS is always the
// mobile device family regardless of touch capability, and other platforms never are - a
// touchscreen Windows/macOS laptop shouldn't count as "mobile". Touch support is only consulted
// as a last resort when no platform signal was available at all.
function getIsMobile(userAgentDataMobile, platformName, hasTouchSupport) {
    if (userAgentDataMobile !== undefined) return userAgentDataMobile
    if (platformName === "Android" || platformName === "iOS") return true
    if (platformName) return false
    return hasTouchSupport
}

// Android tablets omit the "Mobile" token that phones include in their UA string; iOS already
// distinguishes iPad from iPhone/iPod via getIosDeviceFamily. Anything else that reads as mobile
// (e.g. the touch-only fallback with no platform signal) is reported as "mobile" by default.
function getMobileDeviceType(isMobile, platformName, iosDeviceFamily, userAgent) {
    if (!isMobile) return "desktop"
    if (platformName === "iOS") return iosDeviceFamily === "iPad" ? "tablet" : "mobile"
    if (platformName === "Android") return /mobile/i.test(userAgent) ? "mobile" : "tablet"
    return "mobile"
}

async function getBrowserMode() {
    try {
        if (!navigator.storage?.estimate) return "normal"
        const {quota} = await navigator.storage.estimate()
        return typeof quota === "number" && quota < INCOGNITO_QUOTA_THRESHOLD_BYTES ? "incognito" : "normal"
    } catch (e) {
        console.error(e)
        return "normal"
    }
}

// What a third party sees as this client's egress IP. Compared server-side against the
// IP the log request actually arrived from - a mismatch points at a proxy/VPN scoped to this site.
async function getIpViaLookup(timeoutMs = 1500) {
    try {
        const controller = new AbortController()
        const timeout = setTimeout(() => controller.abort(), timeoutMs)

        const response = await fetch("https://api.ipify.org?format=json", {signal: controller.signal})
        clearTimeout(timeout)

        const data = await response.json()
        return data?.ip || null
    } catch (e) {
        console.error(e)
        return null
    }
}

// IPs the browser exposes about itself while gathering WebRTC ICE candidates against a public
// STUN server. Can surface a real IP even through some VPN/proxy setups that don't tunnel UDP.
// Chrome hides host candidates behind mDNS ".local" names by default, so this mostly yields the
// srflx (public) candidate - still useful to cross-check against the lookup IP and server IP.
function getWebRtcIps(timeoutMs = 400) {
    return new Promise((resolve) => {
        try {
            const ips = new Set()
            const pc = new RTCPeerConnection({iceServers: [{urls: "stun:stun.l.google.com:19302"}]})

            pc.onicecandidate = (event) => {
                const candidate = event.candidate?.candidate
                if (!candidate) return

                const match = candidate.match(/(\d{1,3}(?:\.\d{1,3}){3}|[0-9a-f]*:[0-9a-f:]+)/i)
                if (match) ips.add(match[1])
            }

            pc.createDataChannel("")
            pc.createOffer()
                .then(offer => pc.setLocalDescription(offer))
                .catch(() => undefined)

            setTimeout(() => {
                pc.close()
                resolve(Array.from(ips))
            }, timeoutMs)
        } catch (e) {
            console.error(e)
            resolve([])
        }
    })
}

export async function getFingerprintMetadata() {
    try {
        const [fp, botd] = await Promise.all([fpPromise, botdPromise])
        const [fpResult, browserMode, ipLookup, webRtcIps] = await Promise.all([
            fp.get(),
            getBrowserMode(),
            getIpViaLookup(),
            getWebRtcIps(),
        ])
        const botdResult = botd.detect()

        const webGlBasics = componentValue(fpResult?.components?.webGlBasics)
        const webGl = webGlBasics && typeof webGlBasics === "object" ? webGlBasics : undefined
        const rendererUnmasked = webGl?.rendererUnmasked || webGl?.renderer || ""

        const userAgentData = componentValue(fpResult?.components?.userAgentData)
        const touchSupport = componentValue(fpResult?.components?.touchSupport)
        const userAgent = navigator.userAgent || ""
        const maxTouchPoints = navigator.maxTouchPoints || 0
        const hasTouchSupport = Boolean(touchSupport?.touchEvent || touchSupport?.touchStart || (touchSupport?.maxTouchPoints ?? maxTouchPoints) > 0)

        // Independent platform signals, most to least trustworthy. UA-CH is explicit when present
        // (Chromium only); the full UA string beats navigator.platform for non-Chromium browsers,
        // since e.g. Android's navigator.platform ("Linux armv8l") never contains the word
        // "Android" while its UA string reliably does.
        const uaChPlatformName = normalizePlatform(userAgentData?.platform || "", maxTouchPoints)
        const userAgentPlatformName = normalizePlatform(userAgent, maxTouchPoints)
        const navigatorPlatformName = normalizePlatform(navigator.platform || "", maxTouchPoints)
        const rendererPlatformName = inferPlatformFromRenderer(rendererUnmasked)
        const platformName = uaChPlatformName || userAgentPlatformName || navigatorPlatformName

        const uaParser = new UAParser(userAgent)
        const {name: browserName, version: browserVersion} = uaParser.getBrowser()
        const {version: uaParserOsVersion} = uaParser.getOS()

        const iosDeviceFamily = platformName === "iOS" ? getIosDeviceFamily(userAgent, maxTouchPoints) : null
        const isMobile = getIsMobile(userAgentData?.mobile, platformName, hasTouchSupport)

        return {
            deviceId: fpResult?.visitorId,
            deviceIdConfidence: fpResult?.confidence?.score,
            deviceModel: userAgentData?.model
                || iosDeviceFamily
                || getRendererDeviceModelFallback(rendererUnmasked),
            os: platformName,
            osVersion: getOsVersion(platformName, userAgentData, uaParserOsVersion),
            cpuArchitecture: getCpuArchitecture(userAgentData),
            browserName: browserName ?? "unknown",
            browserVersion: browserVersion ?? "unknown",
            userAgent,
            isMobile,
            mobileDeviceType: getMobileDeviceType(isMobile, platformName, iosDeviceFamily, userAgent),
            browserMode,
            ipLookup,
            webRtcIps,
            isMetadataMismatch: getIsMetadataMismatch([
                uaChPlatformName,
                userAgentPlatformName,
                navigatorPlatformName,
                rendererPlatformName,
            ]),
            isBot: botdResult.bot,
            isBotKind: botdResult.bot ? botdResult.botKind : null,
            isEmulator: EMULATOR_RENDERER_PATTERN.test(rendererUnmasked),
            isVirtualMachine: VM_RENDERER_PATTERN.test(rendererUnmasked),
            ...getAiAgentSignals(),
        }
    } catch (e) {
        console.error(e)
        return {error: e.message}
    }
}
